import { useId, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/styles';
import circle from '@turf/circle';
import { map } from '../core/MapView';
import dimensions from '../../common/theme/dimensions';
import { distanceMeters } from '../../common/util/useAgentLocation';

// Room kept clear around the framed pair. The bottom allowance is for the
// status card, which sits over the lower part of the map on every layout.
const FRAME_SIDE = 60;
const FRAME_TOP = 80;
const FRAME_BOTTOM = dimensions.popupMapOffset + 40;
const FRAME_MAX_ZOOM = 17;

// Re-frame when the gap has changed by this much since the last framing, so
// the view tightens as the agent closes in and widens if the vehicle runs.
// The floor keeps GPS jitter from re-framing two points that are on top of
// each other.
const REFRAME_RATIO = 0.3;
const REFRAME_FLOOR_M = 30;

// Draws the recovery agent's own position while a Live session publishes
// it: an accuracy ring, a dot, and a dashed line to the vehicle being
// tracked so the bearing is obvious at a glance. It also owns the camera for
// the session, keeping both the agent and the vehicle in frame. Everything
// clears as soon as the location is withdrawn from the store.
const MapAgentLocation = () => {
  const id = useId();
  const theme = useTheme();

  const location = useSelector((state) => state.session.agentLocation);
  const selectedDeviceId = useSelector((state) => state.devices.selectedId);
  const target = useSelector((state) => state.session.positions[selectedDeviceId]);

  const lastFramedDistance = useRef(null);

  useEffect(() => {
    map.addSource(id, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
    map.addLayer({
      id: `${id}-accuracy`,
      source: id,
      type: 'fill',
      filter: ['==', '$type', 'Polygon'],
      paint: {
        'fill-color': theme.palette.primary.main,
        'fill-opacity': 0.15,
      },
    });
    map.addLayer({
      id: `${id}-line`,
      source: id,
      type: 'line',
      filter: ['==', '$type', 'LineString'],
      paint: {
        'line-color': theme.palette.primary.main,
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    });
    map.addLayer({
      id: `${id}-dot`,
      source: id,
      type: 'circle',
      filter: ['==', '$type', 'Point'],
      paint: {
        'circle-radius': 7,
        'circle-color': theme.palette.primary.main,
        'circle-stroke-color': '#ffffff',
        'circle-stroke-width': 2,
      },
    });
    return () => {
      [`${id}-dot`, `${id}-line`, `${id}-accuracy`].forEach((layer) => {
        if (map.getLayer(layer)) {
          map.removeLayer(layer);
        }
      });
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    };
  }, []);

  useEffect(() => {
    const features = [];
    if (location) {
      const point = [location.longitude, location.latitude];
      if (location.accuracy > 0) {
        features.push(circle(point, location.accuracy / 1000));
      }
      if (target) {
        features.push({
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: [point, [target.longitude, target.latitude]],
          },
        });
      }
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: point },
      });
    }
    map.getSource(id).setData({ type: 'FeatureCollection', features });
  }, [location, target]);

  // Camera. Frame the pair on the first fix of a session, then again only
  // when one of them has drifted out of the clear area or the gap between
  // them has changed enough to matter. Between those moments the user is
  // free to pan and zoom without the map snatching the view back.
  useEffect(() => {
    if (!location) {
      lastFramedDistance.current = null;
      return;
    }
    if (!target) {
      return;
    }

    const canvas = map.getCanvas();
    const width = canvas.clientWidth;
    const height = canvas.clientHeight;
    const base = map.getPadding();
    const padding = {
      top: Math.min(FRAME_TOP, height * 0.15),
      bottom: Math.min(FRAME_BOTTOM, height * 0.5),
      left: FRAME_SIDE,
      right: FRAME_SIDE,
    };

    // Judged against half the padding, so two points sitting right on the
    // framed edge do not count as out of view and trigger an endless loop.
    const inClearArea = (point) => {
      const { x, y } = map.project([point.longitude, point.latitude]);
      return x >= (base.left || 0) + padding.left / 2
        && x <= width - (base.right || 0) - padding.right / 2
        && y >= (base.top || 0) + padding.top / 2
        && y <= height - (base.bottom || 0) - padding.bottom / 2;
    };

    const distance = distanceMeters(location, target);
    const last = lastFramedDistance.current;
    const gapChanged = last === null
      || Math.abs(distance - last) > Math.max(last * REFRAME_RATIO, REFRAME_FLOOR_M);

    if (!gapChanged && inClearArea(location) && inClearArea(target)) {
      return;
    }

    lastFramedDistance.current = distance;
    map.fitBounds([
      [Math.min(location.longitude, target.longitude), Math.min(location.latitude, target.latitude)],
      [Math.max(location.longitude, target.longitude), Math.max(location.latitude, target.latitude)],
    ], {
      padding,
      maxZoom: FRAME_MAX_ZOOM,
      duration: 1000,
    });
  }, [location, target]);

  return null;
};

export default MapAgentLocation;
